import { useCallback, useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import {
  DefaultDescriptionBenefitEducation,
  FormBase,
  FormModal,
  formatUnknownErrorMessage,
  useToast,
} from '@gbs-monorepo-packages/common';
import {
  type RestoreCourseSchema,
  restoreCourseSchema,
} from '@gbs-monorepo/edge/formSchemas/courseSchema';
import { zodResolver } from '@hookform/resolvers/zod';

import { type IDialogModalProps } from '../../../../components/DialogModal';
import {
  type ICourseDTO,
  type ICourseHistory,
  type ICourseVersion,
  getCourseHistory,
} from '../../../../services/courses';
import {
  CheckIcon,
  CheckIconContainer,
  HistoryIcon,
  HistoryIconContainer,
  HistoryVersionDate,
  HistoryVersionDateContainer,
  HistoryVersionItem,
  HistoryVersionItemsContainer,
  Loading,
  LoadingContainer,
  NoHistoryText,
} from './styles';

interface ICoursePageModalProps
  extends Partial<
    Omit<IDialogModalProps, 'children' | 'onAccept' | 'onOpenChange' | 'open'>
  > {
  onAccept: (courseVersion: ICourseVersion) => Promise<void> | void;
  onDecline: () => void;
  open: boolean;
  course?: ICourseDTO | null;
  waitToOpen?: boolean;
}

export const RestorePageModal = ({
  onAccept,
  onDecline,
  open,
  course,
  waitToOpen = false,
  ...props
}: ICoursePageModalProps): JSX.Element | null => {
  const { addToast } = useToast();
  const [loadingCourseHistory, setLoadingCourseHistory] = useState(true);
  const [isRestoring, setIsRestoring] = useState(false);
  const loadedHistoryOnce = useRef(false);

  const [courseHistory, setCourseHistory] = useState<ICourseHistory | null>(
    null
  );

  const restoreCourseForm = useForm<RestoreCourseSchema>({
    resolver: zodResolver(restoreCourseSchema),
    defaultValues: {
      courseVersionId: 0,
    },
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const {
    handleSubmit,
    setValue,
    reset,
    formState: { isValid },
    watch,
  } = restoreCourseForm;

  const values = watch();

  useEffect(() => {
    if (open) {
      reset({
        courseVersionId: 0,
      });
      loadedHistoryOnce.current = false;
    }
  }, [open, reset]);

  const handleRestoreCourse: SubmitHandler<RestoreCourseSchema> = useCallback(
    async (data: RestoreCourseSchema) => {
      if (!data.courseVersionId || !courseHistory?.data || isRestoring) {
        return;
      }

      setIsRestoring(true);
      const courseVersion = courseHistory.data.find(
        (courseVersion) => courseVersion.id === data.courseVersionId
      );

      if (!courseVersion) {
        setIsRestoring(false);
        return;
      }

      try {
        await onAccept?.(courseVersion);
      } catch (error) {
        addToast({
          title: formatUnknownErrorMessage(error),
          description: DefaultDescriptionBenefitEducation,
          styleType: 'error',
          dataCy: 'error-toast',
        });
      } finally {
        setIsRestoring(false);
        reset({
          courseVersionId: 0,
        });
      }
    },
    [addToast, courseHistory?.data, isRestoring, onAccept, reset]
  );

  const handleDeclineRestoreCourse = useCallback(() => {
    if (isRestoring) {
      return;
    }
    onDecline?.();
    loadedHistoryOnce.current = false;
    setCourseHistory(null);
    setIsRestoring(false);
    reset({
      courseVersionId: 0,
    });
  }, [onDecline, reset, isRestoring]);

  useEffect(() => {
    let mount = true;

    const loadCourseVersionsOnce = async () => {
      loadedHistoryOnce.current = true;
      setLoadingCourseHistory(true);
      try {
        if (!course) {
          addToast({
            title: 'Error loading course restore',
            description: DefaultDescriptionBenefitEducation,
            styleType: 'error',
          });
          handleDeclineRestoreCourse();
          return;
        }

        if (course.id > 0) {
          const data: ICourseHistory = await getCourseHistory({
            courseId: course.id,
          });

          // order by createdAt desc
          data.data.sort((a, b) => {
            if (a.createdAt > b.createdAt) {
              return -1;
            }
            if (a.createdAt < b.createdAt) {
              return 1;
            }
            return 0;
          });

          if (mount) {
            setCourseHistory(data);
          }
        }
      } catch (err) {
        loadedHistoryOnce.current = false;
      } finally {
        setLoadingCourseHistory(false);
      }
    };

    if (open && !loadedHistoryOnce.current && course) {
      void loadCourseVersionsOnce();
    }

    return () => {
      mount = false;
    };
  }, [open, course, addToast, handleDeclineRestoreCourse]);

  return !open ? null : (
    <FormBase.Provider {...restoreCourseForm}>
      <FormModal
        acceptText="Restore"
        dataCy="restore-course-dialogModal"
        declineText="Cancel"
        mainText={`${course?.title ?? 'Untitled'} Restore`}
        {...props}
        onAccept={handleSubmit(handleRestoreCourse)}
        onDecline={handleDeclineRestoreCourse}
        onOpenChange={handleDeclineRestoreCourse}
        open={!waitToOpen}
        disabled={!isValid || isRestoring}
      >
        {loadingCourseHistory ? (
          <LoadingContainer data-cy="loading-course-restore">
            <Loading />
          </LoadingContainer>
        ) : (
          <HistoryVersionItemsContainer>
            {courseHistory?.data && courseHistory?.data.length > 0 ? (
              courseHistory?.data.map((courseVersion: ICourseVersion) => (
                <HistoryVersionItem
                  key={courseVersion.id}
                  onClick={() => {
                    setValue('courseVersionId', courseVersion.id, {
                      shouldValidate: true,
                    });
                  }}
                >
                  <HistoryIconContainer>
                    <HistoryIcon />
                  </HistoryIconContainer>
                  <HistoryVersionDateContainer>
                    <HistoryVersionDate>
                      Version from{' '}
                      {new Date(courseVersion.createdAt).toLocaleString()}
                    </HistoryVersionDate>
                  </HistoryVersionDateContainer>
                  <CheckIconContainer>
                    {values.courseVersionId === courseVersion.id && (
                      <CheckIcon />
                    )}
                  </CheckIconContainer>
                </HistoryVersionItem>
              ))
            ) : (
              <NoHistoryText>No course history available.</NoHistoryText>
            )}
          </HistoryVersionItemsContainer>
        )}
      </FormModal>
    </FormBase.Provider>
  );
};
